import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap, finalize, startWith, share } from 'rxjs/operators';
import {
  OrganizationsService,
  Org,
  UpdateOrgSettingsRequest,
  OrgBody,
} from 'ldt-identity-service-api';
import { NotificationService } from '../notification-service/notification.service';
import { Log } from 'src/app/logger.service';

@Injectable({
  providedIn: 'root',
})
export class OrgsService {
  private orgsSource = new BehaviorSubject<Org[] | null>(null);
  private orgsMapSource = new BehaviorSubject<Map<string, Org> | null>(null);
  private refreshing = new BehaviorSubject<boolean>(false);

  orgs$ = this.orgsSource.asObservable().pipe(
    startWith(null),
    tap(() => {
      if (!this.orgsSource.value) {
        this.getOrgs();
      }
    })
  );

  orgsMap$ = this.orgsMapSource.asObservable().pipe(
    startWith(null),
    tap(() => {
      if (!this.orgsMapSource.value) {
        this.getOrgs();
      }
    })
  );

  refreshing$ = this.refreshing.asObservable();

  constructor(
    private orgService: OrganizationsService,
    private notify: NotificationService
  ) {}

  public refresh() {
    this.getOrgs();
  }

  private getOrgs() {
    if (this.refreshing.value) {
      return;
    }

    this.refreshing.next(true);

    this.orgService
      .getOrgs()
      .pipe(
        tap((orgs) => {
          // Create a map for quick lookup by org_id
          const orgsMap = new Map<string, Org>();
          orgs.forEach((org) => {
            orgsMap.set(org.id, org);
          });

          // Update both subjects
          this.orgsSource.next(orgs);
          this.orgsMapSource.next(orgsMap);
        }),
        share(),
        catchError((error) => {
          this.notify.error('Error loading organizations');
          Log.e('OrgsService: getOrgs: error:', error);
          return of([]);
        }),
        finalize(() => this.refreshing.next(false))
      )
      .subscribe();
  }

  public getOrgById(orgId: string): Observable<Org | undefined> {
    return this.orgsMap$.pipe(map((orgsMap) => orgsMap?.get(orgId)));
  }

  public getOrgNameById(orgId: string): Observable<string> {
    return this.getOrgById(orgId).pipe(map((org) => org?.name || orgId));
  }

  public createOrg(orgBody: OrgBody): Observable<Org> {
    return this.orgService.createOrg(orgBody).pipe(
      tap(() => {
        this.refresh();
      }),
      catchError((error) => {
        Log.e('OrgsService: createOrg: error:', error);
        throw error;
      })
    );
  }

  public getOrg(orgId: string): Observable<Org> {
    return this.orgService.getOrg(orgId).pipe(
      tap((org) => {
        // Sync the fresh org into the session cache (map and list) so grids
        // and name lookups reflect it without a full refetch
        const orgsMap = this.orgsMapSource.value;
        if (orgsMap) {
          orgsMap.set(orgId, org);
          this.orgsMapSource.next(orgsMap);
        }
        const orgs = this.orgsSource.value;
        if (orgs) {
          const index = orgs.findIndex((o) => o.id === orgId);
          if (index >= 0) {
            // New array reference so bound grids register the change
            const updated = [...orgs];
            updated[index] = org;
            this.orgsSource.next(updated);
          }
        }
      }),
      catchError((error) => {
        Log.e('OrgsService: getOrg: error:', error);
        throw error;
      })
    );
  }

  public updateOrgSettings(orgId: string, body: UpdateOrgSettingsRequest): Observable<Org> {
    return this.orgService.updateOrgSettings(orgId, body).pipe(
      tap(() => {
        this.refresh();
      }),
      catchError((error) => {
        Log.e('OrgsService: updateOrgSettings: error:', error);
        throw error;
      })
    );
  }
}
